import { MyersBriggsShortToLong, MyersBriggsTypes, responsePromptsData } from "~/data/responsePromptsData";
import {
  StDropdown,
  StDropdownItem,
  StDropdownPortal,
  StDropdownSub,
  StDropdownSubContent,
  StDropdownSubTrigger,
  StDropdownTrigger,
} from "@storytell/ui/dropdown";
import { TbClipboardCopy, TbHelpCircle, TbMoodHappy, TbPencil, TbSearch, TbUsers } from "solid-icons/tb";
import { type Component, For, Show, createSignal } from "solid-js";
import { dispatchGlobalEvent } from "@storytell/events";
import { PmNodeType } from "@storytell/types/richText";

export const TextSelectionMenu: Component<{
  rect: DOMRect | undefined;
  getText: () => string;
  onClose?: () => void;
  isReadOnly: boolean;
}> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);

  // Update open state when rect changes
  const open = () => !!props.rect;

  const onItemSelected = () => {
    getSelection()?.removeAllRanges();
    setIsOpen(false);
    props.onClose?.();
  };

  const submitPrompt = (text: string) => {
    dispatchGlobalEvent("submit-prompt", {
      prompt: {
        type: PmNodeType.Doc,
        content: [{ type: PmNodeType.Paragraph, content: [{ type: PmNodeType.Text, text }] }],
      },
      textPrompt: text,
    });
    // promptBarContext.submission.handleSubmitWithContent(
    //   {
    //     parts: [{ text }],
    //   },
    //   text,
    // );
    onItemSelected();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // If the dropdown is being closed, call onClose to clear the rect
    if (!open) {
      props.onClose?.();
    }
  };

  return (
    <Show when={props.rect}>
      <StDropdown
        open={open()}
        onOpenChange={handleOpenChange}
        modal={false}
        contentProps={{ class: "outline-none" }}
        content={
          <div class="flex flex-col min-w-[280px] max-w-[320px]">
            <Show when={!props.isReadOnly}>
              {/* Help me understand this */}
              <StDropdownSub overlap gutter={8} defaultOpen={false}>
                <StDropdownSubTrigger class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer">
                  <TbHelpCircle class="size-4" />
                  Help me understand this
                  <span class="ml-auto">›</span>
                </StDropdownSubTrigger>
                <StDropdownPortal>
                  <StDropdownSubContent class="hidden data-expanded:block min-w-[300px] bg-tile border border-primary-border rounded-lg shadow-xl px-2 py-1 z-50 outline-none">
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.explainLikeImFive(props.getText()))}
                    >
                      Explain this to me like I'm five years old
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.explainLikeImTwelve(props.getText()))}
                    >
                      Explain this to me like I'm in middle school
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.explainLikeImSixteen(props.getText()))}
                    >
                      Explain this to me like I'm in high school
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.whyDoesThisMatter(props.getText()))}
                    >
                      Why does this matter?
                    </StDropdownItem>
                  </StDropdownSubContent>
                </StDropdownPortal>
              </StDropdownSub>

              {/* Get a more in-depth answer */}
              <StDropdownSub overlap gutter={8} defaultOpen={false}>
                <StDropdownSubTrigger class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer">
                  <TbSearch class="size-4" />
                  Get a more in-depth answer
                  <span class="ml-auto">›</span>
                </StDropdownSubTrigger>
                <StDropdownPortal>
                  <StDropdownSubContent class="hidden data-expanded:block min-w-[280px] bg-tile border border-primary-border rounded-lg shadow-xl px-2 py-1 z-50 outline-none">
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.getMoreDetail(props.getText()))}
                    >
                      Get more detail on this
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.findMoreLikeThis(props.getText()))}
                    >
                      Find similar content like this
                    </StDropdownItem>
                  </StDropdownSubContent>
                </StDropdownPortal>
              </StDropdownSub>

              {/* Rewrite this for a different audience */}
              <StDropdownSub overlap gutter={8} defaultOpen={false}>
                <StDropdownSubTrigger class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer">
                  <TbPencil class="size-4" />
                  Rewrite this for a different audience
                  <span class="ml-auto">›</span>
                </StDropdownSubTrigger>
                <StDropdownPortal>
                  <StDropdownSubContent class="hidden data-expanded:block min-w-[280px] bg-tile border border-primary-border rounded-lg shadow-xl px-2 py-1 z-50 outline-none">
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.rewriteForBrevity(props.getText()))}
                    >
                      For brevity
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.rewriteForExecutive(props.getText()))}
                    >
                      For an executive audience
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.rewriteForLessTechnical(props.getText()))}
                    >
                      For a less technical audience
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.rewriteForMoreTechnical(props.getText()))}
                    >
                      For a more technical audience
                    </StDropdownItem>

                    {/* Nested submenu for personality types */}
                    <StDropdownSub overlap gutter={8} defaultOpen={false}>
                      <StDropdownSubTrigger class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer">
                        <TbUsers class="size-4" />
                        Rewrite for personality type
                        <span class="ml-auto">›</span>
                      </StDropdownSubTrigger>
                      <StDropdownPortal>
                        <StDropdownSubContent class="hidden data-expanded:block min-w-[280px] max-h-[400px] overflow-y-auto bg-tile border border-primary-border rounded-lg shadow-xl px-2 py-1 z-50 outline-none">
                          <div class="px-2 py-1 text-xs text-secondary-text font-medium">
                            Myers Briggs Personality Types:
                          </div>
                          <For each={MyersBriggsTypes}>
                            {(type) => (
                              <StDropdownItem
                                class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                                onSelect={() =>
                                  submitPrompt(responsePromptsData.rewriteForMyersBriggs(props.getText(), type))
                                }
                              >
                                <span class="font-bold">{type}</span> - {MyersBriggsShortToLong(type)}
                              </StDropdownItem>
                            )}
                          </For>
                        </StDropdownSubContent>
                      </StDropdownPortal>
                    </StDropdownSub>
                  </StDropdownSubContent>
                </StDropdownPortal>
              </StDropdownSub>

              {/* Analyze feelings and needs */}
              <StDropdownSub overlap gutter={8} defaultOpen={false}>
                <StDropdownSubTrigger class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer">
                  <TbMoodHappy class="size-4" />
                  Analyze feelings and needs
                  <span class="ml-auto">›</span>
                </StDropdownSubTrigger>
                <StDropdownPortal>
                  <StDropdownSubContent class="hidden data-expanded:block min-w-[320px] bg-tile border border-primary-border rounded-lg shadow-xl px-2 py-1 z-50 outline-none">
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.analyzeFeelingsEmotions(props.getText()))}
                    >
                      What feelings and emotions were expressed?
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.analyzeMetUnmetNeeds(props.getText()))}
                    >
                      What met or unmet needs were shared?
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.analyzeGoneBetter(props.getText()))}
                    >
                      How could this have gone better?
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.analyzePersonInner(props.getText()))}
                    >
                      What's really happening inside this person right now?
                    </StDropdownItem>
                    <StDropdownItem
                      class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                      onSelect={() => submitPrompt(responsePromptsData.analyzeUnderSurface(props.getText()))}
                    >
                      What's happening under the surface here?
                    </StDropdownItem>
                  </StDropdownSubContent>
                </StDropdownPortal>
              </StDropdownSub>

              {/* Ask a follow-up question */}
              {/* <StDropdownItem
                class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
                onSelect={() => submitPrompt(responsePromptsData.askFollowUp(props.getText()))}
              >
                Ask a follow-up question about this
              </StDropdownItem> */}

              <div class="h-px bg-primary-border my-1" />
            </Show>

            {/* Copy text (always visible) */}
            <StDropdownItem
              class="w-full text-left px-3 py-2 text-sm hocus:bg-canvas-hover rounded-md flex items-center gap-2 kb-highlighted:bg-canvas-hover outline-none select-none cursor-pointer"
              onSelect={() => {
                navigator.clipboard.writeText(props.getText());
                onItemSelected();
              }}
            >
              <TbClipboardCopy class="size-4" />
              Copy text
            </StDropdownItem>
          </div>
        }
      >
        <StDropdownTrigger
          as="div"
          class="pointer-events-none fixed"
          style={{
            top: `${props.rect?.top || 0}px`,
            left: `${props.rect?.left || 0}px`,
            width: `${props.rect?.width || 0}px`,
            height: `${props.rect?.height || 0}px`,
          }}
          aria-hidden="true"
        />
      </StDropdown>
    </Show>
  );
};
